import { hasAnyClass } from "../utility/UtilityFunctions";

export default class OffcanvasCheckState {
    constructor() {
        this.elements = {
            html: document.documentElement,
            body: document.body,
            pageWrap: document.querySelector('.s-page-wrap'),
            menuButton: document.querySelector('.c-menu-button'),
            bookButton: document.querySelector('.c-page-header__book'),
            searchModal: document.getElementById("search-modal"),
            searchButton: document.getElementById('search-modal-button'),
            bookCloseButton: document.querySelector('.js-booking-overlay-close'),
            searchCloseButton: document.querySelector('.c-search-modal__close')
        };

        this.classes = {
            active: 'is-active',
            bookActive: 'booking-overlay-is-active',
            menuActive: 'navigation-is-active',
            searchActive: 'search-is-active',
        };

        this.bindEventListeners();
    }

    bindEventListeners() {
        const { pageWrap, menuButton, bookButton, searchModal, searchButton, bookCloseButton, searchCloseButton } = this.elements;

        pageWrap.addEventListener('click', (event) => this.pageWrapClose(event));
        menuButton.addEventListener('click', (event) => this.menuCheckState(event));
        bookButton.addEventListener('click', (event) => this.bookCheckState(event));
        
        // Handle search open & close button / book modal close button
        bookCloseButton?.addEventListener('click', (event) => this.closeBooking());
        searchButton?.addEventListener('click', (event) => this.searchCheckState(event));
        searchCloseButton?.addEventListener('click', (event) => this.closeSearch());
        searchModal?.addEventListener('click', (event) => {
            if (event.target === searchModal) {
                this.closeSearch();
            }
        });
    }

    // Utility function to add or remove classes in batch
    updateClasses(elements, className, shouldAdd) {
        elements.forEach(element => shouldAdd ? element.classList.add(className) : element.classList.remove(className));
    }

    removeClasses(classes) {
        classes.forEach(className => {
            this.elements.html.classList.remove(className);
            this.elements.body.classList.remove(className);
        });
    }

    toggleAria(targetButton, forceState = null) {
        const isExpanded = targetButton.getAttribute('aria-expanded') === 'true';
        targetButton.setAttribute('aria-expanded', forceState !== null ? forceState : !isExpanded);
    }

    menuCheckState(event) {
        event.preventDefault();
        const { html, body, menuButton, bookButton, searchButton } = this.elements;
        const { bookActive, menuActive, searchActive } = this.classes;

        // Clear conflicting states (book and search)
        this.removeClasses([searchActive, bookActive]);

        // Toggle the menuActive class
        this.updateClasses([html, body], menuActive, !body.classList.contains(menuActive));

        // Toggle ARIA attribute for menuButton and ensure other ARIA attributes are reset
        this.toggleAria(menuButton);
        this.toggleAria(bookButton, false);
        this.toggleAria(searchButton, false);
    }

    bookCheckState(event) {
        event.preventDefault();
        const { body, menuButton, bookButton, searchButton } = this.elements;
        const { bookActive, menuActive, searchActive } = this.classes;

        // Clear conflicting states (menu and search)
        this.removeClasses([menuActive, searchActive]);

        // Toggle the bookActive class
        this.updateClasses([body], bookActive, !body.classList.contains(bookActive));

        // Toggle ARIA attribute for bookButton and ensure other ARIA attributes are reset
        this.toggleAria(bookButton);
        this.toggleAria(menuButton, false);
        this.toggleAria(searchButton, false);
    }

    searchCheckState(event) {
        event.preventDefault();
        const { html, body, searchButton, menuButton, bookButton } = this.elements;
        const { bookActive, menuActive, searchActive } = this.classes;

        // Clear conflicting states (menu and book)
        this.removeClasses([menuActive, bookActive]);

        // Toggle the searchActive class
        this.updateClasses([html, body], searchActive, !body.classList.contains(searchActive));

        // Toggle ARIA attribute for searchButton and ensure other ARIA attributes are reset
        this.toggleAria(searchButton);
        this.toggleAria(menuButton, false);
        this.toggleAria(bookButton, false);

        if (!body.classList.contains(searchActive)) {
            this.triggerEvent('revealOffcanvas');
        }
    }

    closeMenu() {
        const { menuButton } = this.elements;
        const { menuActive } = this.classes;

        // Close menu and reset ARIA attribute
        this.removeClasses([menuActive]);
        this.toggleAria(menuButton, false);
    }

    closeBooking() {
        const { bookButton } = this.elements;
        const { bookActive } = this.classes;

        // Close booking overlay and reset ARIA attribute
        this.removeClasses([bookActive]);
        this.toggleAria(bookButton, false);
    }

    closeSearch() {
        const { searchButton } = this.elements;
        const { searchActive } = this.classes;

        // Close search modal and reset ARIA attribute
        this.removeClasses([searchActive]);
        this.toggleAria(searchButton, false);
    }

    pageWrapClose(event) {
        const { body, menuButton, bookButton, searchButton } = this.elements;
        const { bookActive, menuActive, searchActive } = this.classes;
    
        // Check if any of the overlays are active, and if so, close them
        if (hasAnyClass(body, [bookActive, menuActive, searchActive])) {
            this.removeClasses([bookActive, menuActive, searchActive]);
    
            // Reset ARIA attributes for all buttons
            this.toggleAria(menuButton, false);
            this.toggleAria(bookButton, false);
            this.toggleAria(searchButton, false);
        }
    }

    triggerEvent(eventName) {
        const event = new CustomEvent(eventName);
        document.dispatchEvent(event);
    }
}
